import { useEffect, useRef, useState } from 'react'

export default function useScrollDirection(options = {}) {
  const {
    enabled = true,
    threshold = 8,
    thresholdUp,
    thresholdDown,
    resetKey,
    initialDirection = 'up',
    containerRef,
  } = options

  const [direction, setDirection] = useState(initialDirection)
  const lastYRef = useRef(0)
  const tickingRef = useRef(false)

  useEffect(() => {
    if (!enabled) return
    if (typeof window === 'undefined') return

    const target = containerRef?.current || window
    const y =
      target === window
        ? window.pageYOffset || document.documentElement.scrollTop || 0
        : target.scrollTop || 0

    lastYRef.current = y
    setDirection(initialDirection)
    tickingRef.current = false
  }, [resetKey, enabled, containerRef, initialDirection])

  useEffect(() => {
    if (!enabled) {
      setDirection(initialDirection)
      return
    }

    if (typeof window === 'undefined') return

    const getTarget = () => {
      return containerRef?.current || window
    }

    const getScrollTop = (target) => {
      if (target === window) {
        return window.pageYOffset || document.documentElement.scrollTop || 0
      }

      return target.scrollTop || 0
    }

    const target = getTarget()
    lastYRef.current = getScrollTop(target)

    const onScroll = () => {
      if (tickingRef.current) return
      tickingRef.current = true

      window.requestAnimationFrame(() => {
        const currentTarget = getTarget()
        const y = getScrollTop(currentTarget)
        const delta = y - lastYRef.current

        const thresholdForDirection = delta > 0 ? (thresholdDown ?? threshold) : (thresholdUp ?? threshold)

        if (Math.abs(delta) >= thresholdForDirection) {
          setDirection(delta > 0 ? 'down' : 'up')
          lastYRef.current = y
        }

        tickingRef.current = false
      })
    }

    target.addEventListener('scroll', onScroll, { passive: true })

    return () => {
      target.removeEventListener('scroll', onScroll)
    }
  }, [enabled, threshold, thresholdUp, thresholdDown, initialDirection, containerRef])

  return direction
}
